import React from "react"
import Modal from "@kit/design/modal"
import Button from "@kit/design/button"
import Icon from "@kit/design/icon"
import { useVerticalNavOptIn } from "./utils/use-vertical-nav-optin"
import { navName, trackNavClick } from "./utils/track-nav-click"
import OldNavIllustration from "./assets/oldnav.svg"
import NewNavIllustration from "./assets/newnav.svg"

const LEARN_MORE_URL =
  "https://help.kit.com/en/articles/15693313-kit-vertical-navigation"

// The modal is desktop-only (the opt-in is gated to `md`+), so `is_mobile` is
// always false here.
const trackChoice = label =>
  trackNavClick({
    name: navName(label),
    text: label,
    elementType: "button",
    location: "vertical_nav_optin_modal",
    isMobile: false
  })

export function VerticalNavOptInModal() {
  const { shouldShowModal, chooseOldNav, enableVerticalNav } =
    useVerticalNavOptIn()

  if (!shouldShowModal) return null

  return (
    <Modal
      ariaLabel="Meet Kit's new navigation"
      width={720}
      hideHeader
      hideCloseButton
      fullBleed
      onDismiss={undefined}
      onClose={() => {}}
      className="rounded-xl overflow-hidden"
    >
      <div className="flex flex-col items-center gap-8 p-8">
        <div className="flex w-full flex-col items-center gap-4">
          <p className="text-xs font-semibold uppercase leading-4 text-gray-700">
            UI update
          </p>
          <h1 className="text-center font-heading text-5xl leading-none text-gray-900">
            Meet Kit&rsquo;s new navigation
          </h1>
          <div className="flex flex-col gap-6 text-center text-base leading-6 text-gray-900">
            <p>
              We&rsquo;ve redesigned the navigation to help you find what you
              need faster.
            </p>
            <p>
              The new experience is organized around how creators actually work,
              with a cleaner layout and a collapsible sidebar that gives you
              more room to focus when you need it. Creators in testing found
              what they needed 40% faster.{" "}
              <a
                href={LEARN_MORE_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline"
              >
                Learn more
              </a>
            </p>
          </div>
        </div>
        <div className="flex w-full items-center justify-center gap-4">
          <div className="flex flex-1 flex-col items-start">
            <div className="flex w-full items-center justify-center p-2">
              <p className="text-xs font-semibold leading-5 text-gray-900">
                BEFORE
              </p>
            </div>
            <OldNavIllustration className="h-auto w-full" aria-hidden />
            <div className="w-full pt-6">
              <Button
                is="outlined"
                size="lg"
                className="w-full justify-center"
                onClick={() => {
                  trackChoice("Continue with old navigation")
                  chooseOldNav()
                }}
              >
                Continue with old navigation
              </Button>
            </div>
          </div>
          <Icon name="long-arrow-right" className="shrink-0 text-gray-900" />
          <div className="flex flex-1 flex-col items-start">
            <div className="flex w-full items-center justify-center p-2">
              <p className="text-xs font-semibold leading-5 text-gray-900">
                AFTER
              </p>
            </div>
            <NewNavIllustration className="h-auto w-full" aria-hidden />
            <div className="w-full pt-6">
              <Button
                is="primary"
                size="lg"
                className="w-full justify-center"
                onClick={() => {
                  trackChoice("Try the new navigation")
                  enableVerticalNav()
                }}
              >
                Try the new navigation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
